/* Pick keys to be required */
type PickRequired<T, K extends keyof T> = Required<Pick<T, K>>

export namespace Polymorphic {
  /** Maps our name to the HTMLElement ref type */
  export type Ref<
    Names extends Record<keyof Names, React.ElementType>,
    T extends keyof Names
  > = React.ComponentPropsWithRef<Names[T]>['ref']

  /* The base prop for any polymorphic component. `role` is prop to switch on. These components also support `as` for custom tag. */
  type PolymorphicProp<
    M extends Record<keyof M, React.ElementType>,
    T extends keyof M
  > = {
    /* Provide custom rendering of the tag i.e RouterLink. WARNING: It is expected you match the tag that Core renders based on `role` if using as a function. */
    as?: (props: React.ComponentPropsWithRef<M[T]>) => React.ReactElement | null
    /* The primary name to determine semantic intent, this can change the rendered tag */
    role: T
  }

  /* Applies HTML props and component's props and prefers component's */
  type CleanComponentProps<
    M extends Record<keyof M, React.ElementType>,
    T extends keyof M,
    Props
  > = Omit<
    React.ComponentPropsWithoutRef<M[T]>,
    keyof (PolymorphicProp<M, T> & Props)
  >

  /* Make required HTML props */
  type RequiredComponentProps<
    M extends Record<keyof M, React.ElementType>,
    T extends keyof M,
    Require extends keyof React.ComponentPropsWithoutRef<M[T]>
  > = PickRequired<React.ComponentPropsWithoutRef<M[T]>, Require>

  /* Determine the required and optional HTML props along with additional component props. Has no opinion on children. */
  export type ComponentProps<
    /* Map poly name to HTML tag */
    Names extends Record<keyof Names, React.ElementType>,
    /* Name that will determine HTML props */
    Role extends keyof Names,
    /* The HTML props that will be required */
    Require extends keyof React.ComponentPropsWithoutRef<Names[Role]>,
    /* Additional component props */
    Props = {}
  > = PolymorphicProp<Names, Role> &
    RequiredComponentProps<Names, Role, Require> &
    CleanComponentProps<Names, Role, Props> &
    Props

  /* Determine the required and optional HTML props along with additional component props with a ref. Has no opinion on children. */
  export type ComponentPropsWithRef<
    /* Map poly name to HTML tag */
    Names extends Record<keyof Names, React.ElementType>,
    /* Name that will determine HTML props */
    Role extends keyof Names,
    /* The HTML props that will be required */
    Require extends keyof React.ComponentPropsWithoutRef<Names[Role]>,
    /* Additional component props */
    Props = {}
  > = ComponentProps<Names, Role, Require, Props> & {
    ref?: Ref<Names, Role>
  }
}

export namespace PolymorphicAs {
  export type ComponentPropsWithRef<
    P extends {},
    C extends React.ElementType
  > = P & Omit<React.ComponentPropsWithRef<C>, keyof P>

  export type ComponentPropsWithoutRef<
    P extends {},
    C extends React.ElementType
  > = P & Omit<React.ComponentPropsWithoutRef<C>, keyof P>

  export interface ComponentWithForwardedRef<
    P extends {},
    DC extends React.ElementType
  > {
    <C extends React.ElementType>(
      props: {
        as: C
      } & ComponentPropsWithRef<P, C>
    ): JSX.Element
    (props: ComponentPropsWithRef<P, DC>): JSX.Element
    displayName?: string
  }
}
